import { StatusCodes } from "http-status-codes"
import ResponseError from "utils/ResponseError"
import { v4 as uuid } from "uuid"
import {
  Topup,
  CarrotCardAccount,
  TopupRequest,
  VerifyMicrodepositsRequest,
  IsVerifiedResponse,
  VerifyMicrodepositsResponse,
  CarrotCardLaunchRequest,
  CarrotCardLaunchResponse
} from "types/CarrotCardAccount"

export default class CarrotCardClient {
  baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  async ProcessJsonResponse<T>(response, correlationId): Promise<T> {
    if (response.status === StatusCodes.OK) {
      const responseText = await response.text()
      return responseText ? JSON.parse(responseText) : null
    }
    throw new ResponseError(response, correlationId)
  }

  createJsonHeaders(correlationId): Headers {
    const headers = new Headers()
    headers.append("X-Request-ID", correlationId)
    headers.append("Content-Type", "application/json")
    return headers
  }

  async getTopups(accountId: string): Promise<Topup[]> {
    const correlationId = uuid()
    const response = await fetch(`${this.baseUrl}/api/v2/carrot-card/accounts/${accountId}/topups?limit=10`, {
      method: "GET",
      headers: this.createJsonHeaders(correlationId),
      credentials: "include"
    })
    return this.ProcessJsonResponse<Topup[]>(response, correlationId)
  }

  async getAccount(accountId: string): Promise<CarrotCardAccount> {
    const correlationId = uuid()
    const response = await fetch(`${this.baseUrl}/api/v2/carrot-card/accounts/${accountId}`, {
      method: "GET",
      headers: this.createJsonHeaders(correlationId),
      credentials: "include"
    })
    return this.ProcessJsonResponse<CarrotCardAccount>(response, correlationId)
  }

  async getPrelaunchOrActiveCarrotCardAccount(companyId: string): Promise<CarrotCardAccount> {
    const correlationId = uuid()
    const response = await fetch(`${this.baseUrl}/api/v2/carrot-card/accounts/${companyId}/prelaunchOrActiveAccount`, {
      method: "GET",
      headers: this.createJsonHeaders(correlationId),
      credentials: "include"
    })
    return this.ProcessJsonResponse<CarrotCardAccount>(response, correlationId)
  }

  async createTopup(accountId, { amount, mechanism }: TopupRequest): Promise<Topup> {
    const correlationId = uuid()
    const headers = new Headers()
    headers.append("X-Request-ID", correlationId)
    headers.append("Content-Type", "application/json")
    const body = JSON.stringify({
      amount,
      mechanism
    })
    const response = await fetch(`${this.baseUrl}/api/v2/carrot-card/accounts/${accountId}/topups`, {
      credentials: "include",
      body,
      headers,
      method: "POST"
    })

    return this.ProcessJsonResponse<Topup>(response, correlationId)
  }

  async cancelTopup(accountId: string, topupId: string): Promise<void> {
    const correlationId = uuid()
    await fetch(`${this.baseUrl}/api/v2/carrot-card/accounts/${accountId}/topups/${topupId}`, {
      method: "DELETE",
      headers: this.createJsonHeaders(correlationId),
      credentials: "include"
    })
  }

  async isSourceVerified(accountId: string): Promise<IsVerifiedResponse> {
    const correlationId = uuid()
    const response = await fetch(`${this.baseUrl}/api/v2/carrot-card/accounts/${accountId}/isSourceVerified`, {
      method: "GET",
      headers: this.createJsonHeaders(correlationId),
      credentials: "include"
    })
    return this.ProcessJsonResponse<IsVerifiedResponse>(response, correlationId)
  }

  async verifyMicrodeposits(
    accountId,
    { microdeposit1, microdeposit2 }: VerifyMicrodepositsRequest
  ): Promise<VerifyMicrodepositsResponse> {
    const correlationId = uuid()
    const headers = new Headers()
    headers.append("X-Request-ID", correlationId)
    headers.append("Content-Type", "application/json")
    const body = JSON.stringify({
      microdeposit1,
      microdeposit2
    })
    const response = await fetch(`${this.baseUrl}/api/v2/carrot-card/accounts/${accountId}/verifySource`, {
      credentials: "include",
      body,
      headers,
      method: "POST"
    })

    return this.ProcessJsonResponse(response, correlationId)
  }

  async launchCarrotCard(
    carrotCardAccountId: string,
    launchRequest: CarrotCardLaunchRequest
  ): Promise<CarrotCardLaunchResponse> {
    const correlationId = uuid()
    const headers = new Headers()
    headers.append("X-Request-ID", correlationId)
    headers.append("Content-Type", "application/json")
    const body = JSON.stringify(launchRequest)

    const response = await fetch(`${this.baseUrl}/api/v2/carrot-card/accounts/${carrotCardAccountId}/launch`, {
      credentials: "include",
      body,
      headers,
      method: "POST"
    })

    return this.ProcessJsonResponse<CarrotCardLaunchResponse>(response, correlationId)
  }
}
